import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { $TSFixMe } from 'types/ts-migrate'
import { ActionType } from 'typesafe-actions'
import { AccountingStatsResponse } from './types/AccountingStatsResponse'
import { Base64CsvResponse } from './types/Base64CsvResponse'
import { Base64PdfResponse } from './types/Base64PdfResponse'
import { JsonResponse } from './types/JsonResponse'
import { BillingAgencySettingsResponse } from './types/BillingAgencySettingsResponse'

type TStatementJsonSlice = {
  [id: string]: JsonResponse | null
}

type TStatementPdfSlice = {
  [id: string]: string
}

type TStatementCsvSlice = {
  [id: string]: string
}

export type TAccountingInitialState = {
  bankStatementJson: JsonResponse | null
  bankStatementPdf: string
  bankStatementCsv: string
  customerStatements: $TSFixMe[]
  customerStatementJson: TStatementJsonSlice
  customerStatementPdf: TStatementPdfSlice
  customerStatementCsv: TStatementCsvSlice
  consolidatedStatementJson: TStatementJsonSlice
  consolidatedStatementPdf: TStatementPdfSlice
  consolidatedStatementCsv: TStatementCsvSlice
  portfolioStatementJson: TStatementJsonSlice
  portfolioStatementPdf: TStatementPdfSlice
  portfolioStatementCsv: TStatementCsvSlice
  depositStatementJson: TStatementJsonSlice
  depositStatementPdf: TStatementPdfSlice
  depositStatementCsv: TStatementCsvSlice
  depositBankStatementJson: JsonResponse | null
  depositBankStatementPdf: string
  depositBankStatementCsv: string
  commissionStatementJson: JsonResponse | null
  commissionStatementPdf: string
  commissionStatementCsv: string
  stats: AccountingStatsResponse
  billingAgencySettings: BillingAgencySettingsResponse | null
}

const initialState: TAccountingInitialState = {
  bankStatementJson: null,
  bankStatementPdf: '',
  bankStatementCsv: '',
  customerStatements: [],
  customerStatementJson: {},
  customerStatementPdf: {},
  customerStatementCsv: {},
  consolidatedStatementJson: {},
  consolidatedStatementPdf: {},
  consolidatedStatementCsv: {},
  portfolioStatementJson: {},
  portfolioStatementPdf: {},
  portfolioStatementCsv: {},
  depositStatementJson: {},
  depositStatementPdf: {},
  depositStatementCsv: {},
  depositBankStatementJson: null,
  depositBankStatementPdf: '',
  depositBankStatementCsv: '',
  commissionStatementJson: null,
  commissionStatementPdf: '',
  commissionStatementCsv: '',
  stats: {
    fundsCollectedInLast30Days: {
      total: 0,
      percentageChange30Days: 0,
      creditNotes: 0,
    },
    commissionLast30Days: {
      total: 0,
      percentageChange30Days: 0,
    },
    depositsUnderManagement: {
      total: 0,
    },
  },
  billingAgencySettings: null,
}

const accountApiSlice = createSlice({
  name: 'accountingApi',
  initialState,
  reducers: {
    accountingStats_request: () => {},
    accountingStats_success: (state, { payload }: PayloadAction<AccountingStatsResponse>) => {
      state.stats = payload
    },
    bankStatementJson_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {
      state.bankStatementJson = null
    },
    bankStatementJson_success: (state, { payload }: PayloadAction<{ response: JsonResponse }>) => {
      state.bankStatementJson = payload.response
    },
    bankStatementPdf_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {
      state.bankStatementPdf = ''
    },
    bankStatementPdf_success: (state, { payload }: PayloadAction<{ response: Base64PdfResponse }>) => {
      state.bankStatementPdf = payload.response.pdfBase64
    },
    bankStatementCsv_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {
      state.bankStatementCsv = ''
    },
    bankStatementCsv_success: (state, { payload }: PayloadAction<{ response: Base64CsvResponse }>) => {
      state.bankStatementCsv = payload.response.csvBase64
    },
    // customerStatements_request: state => {
    //   state.customerStatements = []
    // },
    // customerStatements_success: (state, { payload }) => {
    //   state.customerStatements = payload
    // },
    customerStatementJson_request: (state, action: PayloadAction<{ customerId: string; accountId: string }>) => {},
    customerStatementJson_success: (
      state,
      { payload }: PayloadAction<{ response: JsonResponse; customerId: string; accountId: string }>,
    ) => {
      state.customerStatementJson = { [`${payload.customerId}${payload.accountId}`]: payload.response }
    },
    customerStatementPdf_request: (
      state,
      { payload }: PayloadAction<{ customerId: string; accountId: string; start_date: string; end_date: string }>,
    ) => {},
    customerStatementPdf_success: (state, { payload }) => {
      state.customerStatementPdf = { [`${payload.customerId}${payload.accountId}`]: payload.response.pdfBase64 }
    },
    customerStatementCsv_request: (
      state,
      { payload }: PayloadAction<{ customerId: string; accountId: string; start_date: string; end_date: string }>,
    ) => {},
    customerStatementCsv_success: (
      state,
      { payload }: PayloadAction<{ response: Base64CsvResponse; customerId: string; accountId: string }>,
    ) => {
      state.customerStatementCsv = { [`${payload.customerId}${payload.accountId}`]: payload.response.csvBase64 }
    },
    // Tenant Statement PDF/CSV (CU-869dmfep1): requested in place of the Account
    // Statement downloads on a lease's Tenant wallet. The epics resolve them into
    // customerStatementPdf_success / customerStatementCsv_success under the same
    // customer/account key, so the ViewStatement download flow needs no changes.
    tenantStatementPdf_request: (
      state,
      action: PayloadAction<{
        portfolioId: string
        customerId: string
        accountId: string
        start_date: string
        end_date: string
      }>,
    ) => {},
    tenantStatementCsv_request: (
      state,
      action: PayloadAction<{
        portfolioId: string
        customerId: string
        accountId: string
        start_date: string
        end_date: string
      }>,
    ) => {},
    consolidatedStatementJson_request: (
      state,
      action: PayloadAction<{ partyId: string; start_date?: string; end_date?: string }>,
    ) => {},
    consolidatedStatementJson_success: (
      state,
      { payload }: PayloadAction<{ response: JsonResponse; partyId: string }>,
    ) => {
      state.consolidatedStatementJson = { [payload.partyId]: payload.response }
    },
    consolidatedStatementPdf_request: (
      state,
      action: PayloadAction<{ partyId: string; start_date?: string; end_date?: string }>,
    ) => {},
    consolidatedStatementPdf_success: (
      state,
      { payload }: PayloadAction<{ response: Base64PdfResponse; partyId: string }>,
    ) => {
      state.consolidatedStatementPdf = { [payload.partyId]: payload.response.pdfBase64 }
    },
    consolidatedStatementCsv_request: (
      state,
      action: PayloadAction<{ partyId: string; start_date?: string; end_date?: string }>,
    ) => {},
    consolidatedStatementCsv_success: (
      state,
      { payload }: PayloadAction<{ response: Base64CsvResponse; partyId: string }>,
    ) => {
      state.consolidatedStatementCsv = { [payload.partyId]: payload.response.csvBase64 }
    },
    portfolioStatement_request: (
      state,
      action: PayloadAction<{ portfolioId: string; start_date: string; end_date: string }>,
    ) => {},
    portfolioStatement_success: (state, { payload }) => {
      state.portfolioStatementJson[payload.portfolioId] = payload.response
    },
    portfolioStatementPdf_request: (
      state,
      action: PayloadAction<{ portfolioId: string; start_date: string; end_date: string }>,
    ) => {},
    portfolioStatementPdf_success: (
      state,
      { payload }: PayloadAction<{ portfolioId: string; response: Base64PdfResponse }>,
    ) => {
      state.portfolioStatementPdf[payload.portfolioId] = payload.response.pdfBase64
    },
    portfolioStatementCsv_request: (
      state,
      action: PayloadAction<{ portfolioId: string; start_date: string; end_date: string }>,
    ) => {},
    portfolioStatementCsv_success: (
      state,
      { payload }: PayloadAction<{ portfolioId: string; response: Base64CsvResponse }>,
    ) => {
      state.portfolioStatementCsv[payload.portfolioId] = payload.response.csvBase64
    },
    depositStatementJson_request: (
      state,
      action: PayloadAction<{ id: string; accountId: string; start_date?: string; end_date?: string }>,
    ) => {},
    depositStatementJson_success: (
      state,
      { payload }: PayloadAction<{ accountId: string; response: JsonResponse }>,
    ) => {
      state.depositStatementJson = { [payload.accountId]: payload.response }
    },
    depositStatementPdf_request: (
      state,
      action: PayloadAction<{ id: string; accountId: string; start_date?: string; end_date?: string }>,
    ) => {},
    depositStatementPdf_success: (
      state,
      { payload }: PayloadAction<{ accountId: string; response: Base64PdfResponse }>,
    ) => {
      state.depositStatementPdf = { [payload.accountId]: payload.response.pdfBase64 }
    },
    depositStatementCsv_request: (
      state,
      action: PayloadAction<{ id: string; accountId: string; start_date?: string; end_date?: string }>,
    ) => {},
    depositStatementCsv_success: (
      state,
      { payload }: PayloadAction<{ accountId: string; response: Base64CsvResponse }>,
    ) => {
      state.depositStatementCsv = { [payload.accountId]: payload.response.csvBase64 }
    },
    depositBankStatementJson_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    depositBankStatementJson_success: (state, { payload }: PayloadAction<JsonResponse>) => {
      state.depositBankStatementJson = payload
    },
    depositBankStatementPdf_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    depositBankStatementPdf_success: (state, { payload }: PayloadAction<Base64PdfResponse>) => {
      state.depositBankStatementPdf = payload.pdfBase64
    },
    depositBankStatementCsv_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    depositBankStatementCsv_success: (state, { payload }: PayloadAction<Base64CsvResponse>) => {
      state.depositBankStatementCsv = payload.csvBase64
    },
    commissionStatementJson_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    commissionStatementJson_success: (state, { payload }: PayloadAction<{ response: JsonResponse }>) => {
      state.commissionStatementJson = payload.response
    },
    commissionStatementPdf_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    commissionStatementPdf_success: (state, { payload }: PayloadAction<{ response: Base64PdfResponse }>) => {
      state.commissionStatementPdf = payload.response.pdfBase64
    },
    commissionStatementCsv_request: (state, action: PayloadAction<{ start_date?: string; end_date?: string }>) => {},
    commissionStatementCsv_success: (state, { payload }: PayloadAction<{ response: Base64CsvResponse }>) => {
      state.commissionStatementCsv = payload.response.csvBase64
    },
    billingAgencySettings_request: () => {},
    billingAgencySettings_success: (state, { payload }: PayloadAction<BillingAgencySettingsResponse>) => {
      state.billingAgencySettings = payload
    },
    // No slice patch — the epic re-requests, so the read back is the
    // confirmation the write landed on every row.
    updateBillingInvoiceNumber_request: (state, action: PayloadAction<{ number: number }>) => {},
    updateBillingInvoiceNumber_success: () => {},
    // Same read-back pattern: both re-request the settings on success.
    setBillingAgencyDiscounts_request: (
      state,
      action: PayloadAction<{
        agencyId: string
        percentProcessingFeeDiscount: number | null
        percentTransactionFeeDiscount: number | null
        notes: string | null
      }>,
    ) => {},
    setBillingAgencyDiscounts_success: () => {},
    removeBillingAgencySettings_request: (state, action: PayloadAction<{ agencyId: string }>) => {},
    removeBillingAgencySettings_success: () => {},
  },
})

const { reducer, actions: events } = accountApiSlice

export type AccountingEvent = ActionType<typeof events>

export { events, reducer }
